'use client';

import { useClientConfig } from '@shared/utils';
import type { RequestHandler } from 'msw';
import React, { useEffect, useRef } from 'react';
import { initState } from '../../mockServer/initState';

type HandlersFactory = (apiUrl: string) => { handlers: RequestHandler[] };

const initialiseMocking = async (isMockingEnabled: boolean, apiUrl: string, createHandlers: HandlersFactory): Promise<void> => {
  if (typeof window === 'undefined' || !isMockingEnabled) {
    return;
  }

  const { worker } = await import('@shared/api-utils/browser');

  try {
    await worker.start({
      onUnhandledRequest: 'bypass',
    });
  } catch {
    // Worker already started or failed to start
  }

  worker.resetHandlers();
  const { handlers } = createHandlers(apiUrl);
  worker.use(...handlers);
};

function MockClientProvider({
  children,
  createHandlers,
}: Readonly<{
  children: React.ReactNode;
  createHandlers: HandlersFactory;
}>) {
  const { isMockingEnabled, apiUrl } = useClientConfig();
  const [isInitialised, setIsInitialised] = React.useState(!isMockingEnabled);
  const initialisationRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    if (isMockingEnabled && !initialisationRef.current) {
      initialisationRef.current = initialiseMocking(isMockingEnabled, apiUrl, createHandlers)
        .then(() => {
          initState.setClientInitialised();
          setIsInitialised(true);
        })
        .catch(() => {
          setIsInitialised(true);
        })
        .finally(() => {
          initialisationRef.current = null;
        });
    }

    return () => {
      if (typeof window !== 'undefined') {
        import('@shared/api-utils/browser').then(({ cleanupBrowserWorker }) => {
          cleanupBrowserWorker();
        });
      }
    };
  }, [apiUrl, createHandlers, isMockingEnabled]);

  if (isInitialised) {
    return <>{children}</>;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
    >
      <div>Initialising mock server...</div>
    </div>
  );
}

export { MockClientProvider };
